import type { ResolvedEdge } from '../resources/resourceRegistry';
import type { ChangeContext } from '../client/changeManager';
import type { DevtoolsCallbacks } from '../devtools/maybeLoadDevtools';
import type { Logger } from '../devtools/logger';
import type { CacheEntry, CacheMeta } from './entries/cacheEntry';
import type { ResourceDefinition } from '../resources/types';
import { produceNextCacheEntry } from './entries/produceNextCacheEntry';
import { tryMarkStale } from './entries/tryMarkStale';
import { toError } from '../resolvers/definition/resolverState';
import { NodeStore } from './nodeStore';
import { StateEvent } from './resourceLifecycleManager';
import type { IndexedSet } from '../utils/indexedSet';

export type SubscriptionContext = {
  nodeStore: NodeStore;
  schedule: (effect: () => void) => void;
  scheduleInvalidation: (effect: () => void) => void;
  onInvalidated: (instanceKey: string, changeContext: ChangeContext) => void;
  onChange: (
    instanceKey: string,
    entry: CacheEntry<any, any>,
    meta: {
      dataChanged: boolean;
      flagsChanged: boolean;
    }
  ) => void;
  logger?: Logger;
  devtools?: DevtoolsCallbacks;
};

/** A correlated context pair for one tag matched between parent and child. */
export type MatchedContext = { parent: unknown; child: unknown };

const tagsForLink = (
  linkKey: string,
  paramTags: IndexedSet<string> | null | undefined,
  dataTags: IndexedSet<string> | null | undefined
): ReadonlySet<string> | null => {
  const fromParams = paramTags?.get(linkKey);
  const fromData = dataTags?.get(linkKey);

  if (!fromParams) {
    return fromData ?? null;
  }
  if (!fromData) {
    return fromParams;
  }
  return new Set([...fromParams, ...fromData]);
};

/**
 * Reconstruct every matched tag between two instances and the per-side context
 * each contributed, from the tags retained on each node's meta. Only the first
 * matched tag actually drives a propagation (changeId dedup), but the firing
 * transform gets the full set here so it can join across all matches in one
 * pass.
 */
const computeMatchedContexts = (
  linkKey: string,
  parentMeta: CacheMeta<unknown>,
  childMeta: CacheMeta<unknown>
): MatchedContext[] => {
  const parentData = parentMeta.dataLinkTags ?? null;
  const childData = childMeta.dataLinkTags ?? null;

  const parentTags = tagsForLink(
    linkKey,
    parentMeta.paramLinkTags,
    parentData?.tags
  );
  const childTags = tagsForLink(
    linkKey,
    childMeta.paramLinkTags,
    childData?.tags
  );

  if (!parentTags || !childTags) {
    return [];
  }

  const [smaller, larger] =
    parentTags.size <= childTags.size
      ? [parentTags, childTags]
      : [childTags, parentTags];

  const matches: MatchedContext[] = [];
  for (const tag of smaller) {
    if (!larger.has(tag)) {
      continue;
    }
    matches.push({
      parent: parentData?.contexts?.get(linkKey, tag),
      child: childData?.contexts?.get(linkKey, tag),
    });
  }

  return matches;
};

export const establishEdge = (
  ctx: SubscriptionContext,
  childKey: string,
  parentKey: string,
  edge: ResolvedEdge,
  childHash: ResourceDefinition['hash'],
  parentContext: unknown,
  childContext: unknown
) => {
  const parentEmitter = ctx.nodeStore.getEmitter(parentKey);
  const childEmitter = ctx.nodeStore.getEmitter(childKey);

  const offs: (() => void)[] = [];

  const { behavior, resolve } = edge;

  if (behavior.state && resolve) {
    const applyTransform = (
      {
        changeContext,
        dataChanged = true,
        flagsChanged = true,
      }: Partial<StateEvent> & { changeContext: ChangeContext },
      bypassWaveGuard = false
    ) => {
      const childEntry = ctx.nodeStore.get(childKey);
      if (!childEntry) {
        ctx.logger?.error('child entry not found');
        return;
      }

      if (
        !bypassWaveGuard &&
        childEntry.meta.changeContext.changeId >= changeContext.changeId
      ) {
        return;
      }

      const parentEntry = ctx.nodeStore.get(parentKey);
      if (!parentEntry) {
        ctx.logger?.error('parent entry not found');
        return;
      }

      let matchedContexts: MatchedContext[] | undefined;
      const getMatchedContexts = () =>
        (matchedContexts ??= computeMatchedContexts(
          edge.linkKey,
          parentEntry.meta,
          childEntry.meta
        ));

      let result;

      try {
        const nextState =
          resolve({
            dataChanged,
            flagsChanged,
            parent: {
              state: parentEntry.state,
              params: parentEntry.meta.params,
              context: parentContext,
            },
            child: {
              state: childEntry.state,
              params: childEntry.meta.params,
              context: childContext,
            },
            getContexts: getMatchedContexts,
          }) ?? childEntry.state;

        result = produceNextCacheEntry({
          hash: childHash,
          currentEntry: childEntry,
          params: childEntry.meta.params,
          changeContext,
          nextState,
        });
      } catch (error) {
        if (changeContext.optimistic) {
          // Child is left unchanged. On rollback, the parent reverts to its
          // pre-optimistic state and re-derives the child via full propagation.
          // If resolve still throws with the rolled-back data, the non-optimistic
          // error path below will apply the error state to the child.
          ctx.logger?.error('optimistic propagation: resolve threw', {
            error: (error as Error).message,
            parentKey,
            childKey,
          });
          return;
        }

        ctx.logger?.error('error applying transform', {
          error: (error as Error).message,
        });

        const nextState = toError(error as Error)(childEntry.state);
        result = produceNextCacheEntry({
          hash: childHash,
          currentEntry: childEntry,
          params: childEntry.meta.params,
          changeContext,
          nextState,
        });

        if (result.dataChanged || result.flagsChanged) {
          ctx.nodeStore.set(childKey, result.nextEntry);
          ctx.onChange(childKey, result.nextEntry, result);
        }

        // Don't cascade — the error was produced by this edge's resolve,
        // not by the parent's state.
        return;
      }

      if (!result.dataChanged && !result.flagsChanged) {
        return;
      }

      ctx.nodeStore.set(childKey, result.nextEntry);

      return result;
    };

    offs.push(
      parentEmitter.on('state', (event) => {
        ctx.schedule(() =>
          childEmitter.emit('collect', { changeContext: event.changeContext })
        );
      })
    );

    offs.push(
      childEmitter.on('pull', (event) => {
        const parentEntry = ctx.nodeStore.get(parentKey);
        if (!parentEntry) {
          return;
        }

        if (
          parentEntry.meta.changeContext.changeId < event.changeContext.changeId
        ) {
          return;
        }

        const result = applyTransform(
          {
            changeContext: event.changeContext,
            dataChanged: parentEntry.meta.lastChange?.dataChanged,
            flagsChanged: parentEntry.meta.lastChange?.flagsChanged,
          },
          true
        );

        if (!result) {
          return;
        }

        event.result.dataChanged ||= result.dataChanged;
        event.result.flagsChanged ||= result.flagsChanged;

        ctx.devtools?.trace({
          parent: 'propagation',
          type: 'state',
          changeContext: event.changeContext,
          instanceKey: childKey,
          parentInstanceKey: parentKey,
        });
      })
    );

    // Initialization: child requests initialization > parent replays its state
    offs.push(
      childEmitter.on('init', (event) => {
        const changeContext = ctx.nodeStore.get(parentKey)?.meta.changeContext;

        if (
          changeContext &&
          applyTransform({
            changeContext,
          })
        ) {
          event.onInit(parentKey);
        }
      })
    );
  }

  if (edge.behavior.invalidation) {
    const invalidate = (changeContext: ChangeContext) => {
      const childEntry = ctx.nodeStore.get(childKey);

      if (!childEntry) {
        return false;
      }

      const nextEntry = tryMarkStale(childEntry, childHash, changeContext);

      if (!nextEntry) {
        return false;
      }

      ctx.devtools?.trace({
        changeContext,
        instanceKey: childKey,
        parent: 'propagation',
        type: 'invalidation',
        parentInstanceKey: parentKey,
      });

      ctx.nodeStore.set(childKey, nextEntry);

      ctx.onChange(childKey, nextEntry, {
        dataChanged: false,
        flagsChanged: true,
      });

      ctx.onInvalidated(childKey, changeContext);

      ctx.nodeStore
        .getEmitter(childKey)
        .emit('invalidation', { changeContext });

      return true;
    };

    // Invalidation: parent invalidated > invalidate child. Deferred onto the
    // invalidation tier so all of this wave's state propagation settles first;
    // if the child also receives fresh state this wave, tryMarkStale's originId
    // guard then drops this invalidation (see deferLifecycleEffects).
    offs.push(
      parentEmitter.on('invalidation', ({ changeContext }) => {
        if (!changeContext.optimistic) {
          ctx.scheduleInvalidation(() => invalidate(changeContext));
        }
      })
    );

    // Cascading invalidation: parent data changes > mark child stale
    if (!edge.behavior.state) {
      offs.push(
        parentEmitter.on('state', ({ dataChanged, changeContext }) => {
          if (
            dataChanged &&
            changeContext.canCascade &&
            !changeContext.optimistic
          ) {
            ctx.scheduleInvalidation(() => invalidate(changeContext));
          }
        })
      );
    }
  }

  return () => offs.forEach((off) => off());
};
